import React, { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { 
  ExternalLink, 
  Copy, 
  CheckCircle, 
  AlertCircle, 
  Book, 
  Settings, 
  Key,
  ChevronRight,
  ChevronDown,
  Info
} from 'lucide-react';

interface SetupInstructionsProps {
  provider: 'zoho' | 'powerbi';
  onComplete?: () => void;
}

const SetupInstructions: React.FC<SetupInstructionsProps> = ({ provider, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [applicationDetails, setApplicationDetails] = useState({
    clientName: 'Mentoros Analytics Integration',
    homepageUrl: 'https://your-domain.com',
    redirectUri: `${window.location.origin}/analytics/oauth/callback/zoho`
  });

  const handleCopy = async (text: string, identifier: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(identifier);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderZohoInstructions = () => (
    <div className="space-y-6">
      {/* Overview */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Getting Started with Zoho Analytics</h3>
            <p className="text-blue-700 text-sm">
              To connect Zoho Analytics, you'll need to create an OAuth application in the Zoho API Console 
              and configure the necessary permissions. This process typically takes 5-10 minutes.
            </p>
          </div>
        </div>
      </Card>

      {/* Step 1: Create Zoho Application */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            Step 1: Create Zoho Application
          </h3>
          <div className="flex items-center space-x-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              currentStep >= 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {currentStep > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
            </div>
          </div>
        </div>

        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">1. Go to Zoho API Console</span>
              <Button
                onClick={() => window.open('https://api-console.zoho.com/', '_blank')}
                variant="secondary"
                size="sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Console
              </Button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <span className="text-gray-500 min-w-[20px]">2.</span>
                <span>Sign in with your Zoho account</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-gray-500 min-w-[20px]">3.</span>
                <span>Click "Add Client" button</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-gray-500 min-w-[20px]">4.</span>
                <span>Select "Server-based Applications"</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-gray-500 min-w-[20px]">5.</span>
                <div className="flex-1">
                  <span>Fill in the application details:</span>
                  <div className="mt-2 space-y-2">
                    <div className="bg-white border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                      <label className="block text-sm font-medium text-gray-700 px-3 pt-2 pb-1">Client Name:</label>
                      <div className="flex items-center">
                        <input 
                          type="text" 
                          value={applicationDetails.clientName}
                          onChange={(e) => setApplicationDetails(prev => ({ ...prev, clientName: e.target.value }))}
                          placeholder="Enter your application name"
                          className="flex-1 px-3 pb-2 bg-transparent border-0 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                        />
                        <Button
                          onClick={() => handleCopy(applicationDetails.clientName, 'client-name')}
                          variant="secondary"
                          size="sm"
                          className="mr-2"
                        >
                          {copiedText === 'client-name' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                      <label className="block text-sm font-medium text-gray-700 px-3 pt-2 pb-1">Homepage URL:</label>
                      <div className="flex items-center">
                        <input 
                          type="url" 
                          value={applicationDetails.homepageUrl}
                          onChange={(e) => setApplicationDetails(prev => ({ ...prev, homepageUrl: e.target.value }))}
                          placeholder="https://your-domain.com"
                          className="flex-1 px-3 pb-2 bg-transparent border-0 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                        />
                        <Button
                          onClick={() => handleCopy(applicationDetails.homepageUrl, 'homepage-url')}
                          variant="secondary"
                          size="sm"
                          className="mr-2"
                        >
                          {copiedText === 'homepage-url' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                      <label className="block text-sm font-medium text-gray-700 px-3 pt-2 pb-1">Authorized Redirect URIs:</label>
                      <div className="flex items-center">
                        <input 
                          type="url" 
                          value={applicationDetails.redirectUri}
                          onChange={(e) => setApplicationDetails(prev => ({ ...prev, redirectUri: e.target.value }))}
                          placeholder="https://your-domain.com/analytics/oauth/callback/zoho"
                          className="flex-1 px-3 pb-2 bg-transparent border-0 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                        />
                        <Button
                          onClick={() => handleCopy(applicationDetails.redirectUri, 'redirect-uri')}
                          variant="secondary"
                          size="sm"
                          className="mr-2"
                        >
                          {copiedText === 'redirect-uri' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setCurrentStep(2)}
                disabled={currentStep < 1}
              >
                Next: Configure Scopes
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Step 2: Configure Scopes */}
      {currentStep >= 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">
              Step 2: Configure OAuth Scopes
            </h3>
            <div className="flex items-center space-x-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                currentStep >= 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {currentStep > 2 ? <CheckCircle className="w-4 h-4" /> : '2'}
              </div>
            </div>
          </div>

          <Card className="p-6">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Configure the required OAuth scopes for Zoho Analytics access:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="text-sm font-medium mb-2 text-gray-900">Required Scopes:</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm text-gray-900">ZohoAnalytics.metadata.read</span>
                      <span className="text-xs text-gray-600">Read table schemas</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm text-gray-900">ZohoAnalytics.data.read</span>
                      <span className="text-xs text-gray-600">Read data</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm text-gray-900">ZohoAnalytics.modeling.read</span>
                      <span className="text-xs text-gray-600">Read data models</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Combined Scope String:</span>
                      <Button
                        onClick={() => handleCopy(
                          'ZohoAnalytics.metadata.read,ZohoAnalytics.data.read,ZohoAnalytics.modeling.read',
                          'scopes'
                        )}
                        variant="secondary"
                        size="sm"
                      >
                        {copiedText === 'scopes' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <div className="font-mono text-sm mt-1 p-2 bg-white rounded border text-gray-900">
                      ZohoAnalytics.metadata.read,ZohoAnalytics.data.read,ZohoAnalytics.modeling.read
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setCurrentStep(1)}
                  variant="secondary"
                >
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                  Previous
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                >
                  Next: Get Credentials
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 3: Get Credentials */}
      {currentStep >= 3 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">
              Step 3: Get Application Credentials
            </h3>
            <div className="flex items-center space-x-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                currentStep >= 3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {currentStep > 3 ? <CheckCircle className="w-4 h-4" /> : '3'}
              </div>
            </div>
          </div>

          <Card className="p-6">
            <div className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">1.</span>
                  <span>After creating the application, you'll see your credentials</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">2.</span>
                  <span>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">3.</span>
                  <span>Note your Zoho organization ID or email</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">4.</span>
                  <span>Identify your Analytics workspace name</span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-yellow-800">Important Security Note</div>
                    <div className="text-yellow-700 text-sm mt-1">
                      Keep your Client Secret secure and never share it publicly. 
                      Store it safely as you'll need it for the next step.
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setCurrentStep(2)}
                  variant="secondary"
                >
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                  Previous
                </Button>
                <Button
                  onClick={() => setCurrentStep(4)}
                >
                  Next: Configure Application
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 4: Final Configuration */}
      {currentStep >= 4 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">
              Step 4: Configure in Mentoros
            </h3>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-green-100 text-green-700">
                <CheckCircle className="w-4 h-4" />
              </div>
            </div>
          </div>

          <Card className="p-6">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Now you're ready to configure Zoho Analytics in your Mentoros dashboard:
              </p>

              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">1.</span>
                  <span>Return to the Analytics setup page</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">2.</span>
                  <span>Click "Configure" on the Zoho Analytics card</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">3.</span>
                  <span>Enter your Client ID, Client Secret, and workspace details</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">4.</span>
                  <span>Click "Connect Zoho" to start the OAuth flow</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">5.</span>
                  <span>Authorize the application in Zoho</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-gray-500 min-w-[20px]">6.</span>
                  <span>Return to Mentoros to complete the setup</span>
                </div>
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setCurrentStep(3)}
                  variant="secondary"
                >
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                  Previous
                </Button>
                <Button
                  onClick={onComplete}
                >
                  Complete Setup
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Troubleshooting Section */}
      <div className="mt-8">
        <button
          onClick={() => toggleSection('troubleshooting')}
          className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <span className="font-medium text-gray-900 dark:text-black">Troubleshooting Common Issues</span>
          {expandedSection === 'troubleshooting' ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {expandedSection === 'troubleshooting' && (
          <div className="mt-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">OAuth Redirect Issues</h4>
                <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">
                  If the OAuth redirect fails, check:
                </p>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside ml-4">
                  <li>Redirect URI exactly matches the one configured in Zoho</li>
                  <li>No trailing slashes or extra parameters in the URI</li>
                  <li>Using HTTPS in production environments</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Scope Permission Errors</h4>
                <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">
                  If you get permission errors:
                </p>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside ml-4">
                  <li>Ensure all required scopes are configured</li>
                  <li>Check that your Zoho account has access to Analytics</li>
                  <li>Verify workspace permissions in Zoho Analytics</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Connection Timeout</h4>
                <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">
                  If connections timeout:
                </p>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside ml-4">
                  <li>Check your internet connection</li>
                  <li>Verify Zoho service status</li>
                  <li>Try refreshing tokens manually</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPowerBIInstructions = () => (
    <div className="space-y-6">
      {/* Overview */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Getting Started with Power BI</h3>
            <p className="text-blue-700 text-sm">
              To connect Power BI, you'll need to register an application in Azure AD and configure 
              the necessary API permissions. This process requires Azure AD admin privileges.
            </p>
          </div>
        </div>
      </Card>

      {/* Power BI setup steps would go here */}
      <Card className="p-6">
        <div className="text-center py-8">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Power BI Setup</h3>
          <p className="text-text-secondary mb-4">
            Power BI integration setup instructions coming soon.
          </p>
          <Button
            onClick={() => window.open('https://dev.powerbi.com/apps', '_blank')}
            variant="secondary"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Power BI Dev Center
          </Button>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">
            {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} Setup Instructions
          </h2>
          <p className="text-text-secondary mt-1">
            Follow these steps to configure your analytics provider integration.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Book className="w-5 h-5 text-blue-600" />
          <span className="text-sm text-blue-600 font-medium">Step-by-step guide</span>
        </div>
      </div>

      {provider === 'zoho' ? renderZohoInstructions() : renderPowerBIInstructions()}
    </div>
  );
};

export default SetupInstructions;